import React, { useMemo, useRef, type FC, type PropsWithChildren } from 'react'
import { useStore } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { ActionCardsProps, createActionCardsStore, Store } from '../store'
import { ActionCardsConfig, ActionCardsSettings } from '../types'
import transformDataMap from '../utils/transformDataMap'
import { ActionCardData } from '..'
import useMountEffect from '../hooks/useMountEffect'
import { ProjectFileUploadServiceProvider } from '../services/projectFileUploadService'
import { isColorDark } from '../utils/isColorDark'

type AppContextType = {
  settings: ActionCardsSettings
  config: ActionCardsConfig
  prevActionCardsDataRef: React.RefObject<ActionCardData | null>
  rootRef: React.RefObject<HTMLDivElement | null>
  isAccentColorDark: boolean
  highlightedIds: number[]
  setHighlightedIds: (ids: number[]) => void
  resetHighlights: (idsToReset: number[]) => void
}

type ActionCardsStore = ReturnType<typeof createActionCardsStore> & AppContextType

const AppContext = React.createContext<ActionCardsStore | null>(null)

export const AppContextProvider: FC<PropsWithChildren<ActionCardsProps>> = ({ children, ...props }) => {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const prevActionCardsDataRef = useRef<ActionCardData | null>(null)
  const [highlightedIds, setHighlightedIds] = React.useState<number[]>([])

  const resetHighlights = (idsToReset: number[]) => {
    setHighlightedIds(prev => prev.filter(id => !idsToReset.includes(id)))
  }

  const store = useMemo(() => {
    return createActionCardsStore(
      { ...props },
      { prevActionCardsDataRef, nodesVisibilityRules: props.settings?.nodesVisibilityRules },
    )
  }, [JSON.stringify(props.value)])

  const processedSettings = {
    ...props.settings,
    additionalData: transformDataMap(props.settings?.additionalData || {}),
  }

  useMountEffect(() => {
    props.onInit?.(store.getState().actionCardsModel)
  })

  const fileUploadServiceProvider = props.config.fileUploadServiceProvider || new ProjectFileUploadServiceProvider()
  const isAccentColorDark = isColorDark(props.settings?.accentColor || '#000000')
  return (
    <AppContext.Provider
      value={{
        ...store,
        settings: processedSettings,
        config: {
          ...props.config,
          fileUploadServiceProvider,
        },
        prevActionCardsDataRef,
        rootRef,
        isAccentColorDark,
        highlightedIds,
        setHighlightedIds,
        resetHighlights,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export const useAppContext = (): Store & AppContextType => {
  const context = React.useContext(AppContext)

  if (!context) {
    throw new Error('useAppContext must be used within an AppContextProvider')
  }

  return {
    ...useStore(
      context,
      useShallow(state => state),
    ),
    settings: context.settings,
    config: context.config,
    prevActionCardsDataRef: context.prevActionCardsDataRef,
    rootRef: context.rootRef,
    isAccentColorDark: context.isAccentColorDark,
    highlightedIds: context.highlightedIds,
    setHighlightedIds: context.setHighlightedIds,
    resetHighlights: context.resetHighlights,
  }
}
